import React from 'react';
import {
    Autocomplete, TextField, Typography, Box, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * ManageableAutocomplete
 *
 * Autocomplete dropdown with inline delete buttons and an "Add New" option.
 *
 * Props:
 *   label        - TextField label
 *   options      - Array of { id, label, ...any }
 *   value        - Currently selected option object (or null)
 *   onChange      - (option | null) => void
 *   onAdd        - () => void — called when "Add New" is clicked
 *   onDelete     - (option) => void — called when delete icon is clicked
 *   addLabel     - Custom label for the add option (default: "Add New {label}")
 *   colors       - Theme colors object (for styling the add option)
 *   fullWidth    - Pass through to Autocomplete
 *   disabled     - Pass through to Autocomplete
 */
const ManageableAutocomplete = ({
    label,
    options = [],
    value,
    onChange,
    onAdd,
    onDelete,
    addLabel,
    colors,
    fullWidth = true,
    disabled = false,
}) => {
    const ADD_ID = '__add__';
    const allOptions = onAdd
        ? [...options, { id: ADD_ID, label: addLabel || `Add New ${label}` }]
        : options;

    return (
        <Autocomplete
            fullWidth={fullWidth}
            disabled={disabled}
            options={allOptions}
            value={value || null}
            onChange={(_, val) => {
                if (val?.id === ADD_ID) {
                    onAdd?.();
                } else {
                    onChange(val);
                }
            }}
            getOptionLabel={(opt) => opt.label || ''}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            renderOption={(props, opt) => {
                if (opt.id === ADD_ID) {
                    return (
                        <li {...props} key={ADD_ID}>
                            <Typography sx={{ color: colors?.blueAccent?.[400] || 'primary.main', fontWeight: 600 }}>
                                <AddIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                {opt.label}
                            </Typography>
                        </li>
                    );
                }
                return (
                    <li {...props} key={opt.id}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            {opt.label}
                            {onDelete && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(opt);
                                    }}
                                    sx={{ ml: 1 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    </li>
                );
            }}
            renderInput={(params) => <TextField {...params} label={label} />}
            ListboxProps={{ sx: { maxHeight: 300 } }}
            filterOptions={(opts, { inputValue }) => {
                if (!inputValue) return opts;
                const lower = inputValue.toLowerCase();
                return opts.filter(o => o.id === ADD_ID || o.label.toLowerCase().includes(lower));
            }}
        />
    );
};

export default ManageableAutocomplete;
